/**
 * Os icones das ferramentas, desenhados aqui em SVG.
 *
 * Por que nao emoji: emoji e fonte do sistema. Ele muda de desenho, de peso e de cor
 * entre Windows, Android e iOS, ignora a cor de destaque e nao entende tema escuro.
 * Um cinto de ferramentas precisa que o mesmo botao pareca o mesmo botao na lousa da
 * escola e no tablet do aluno.
 *
 * Cada icone e um traco de 24x24, todos com a MESMA gramatica visual: linha continua
 * para objeto que a ferramenta cria, linha pontilhada para objeto que ela usa como
 * referencia, disco cheio para ponto que nasce, anel vazado para ponto que ja existia.
 * Ler o cinto vira, assim, ler a propria construcao.
 *
 * Este modulo e da INTERFACE, nao do kernel: `src/geom/tools.ts` continua sem saber
 * que existe tela. Ele so exporta um nome de glifo (`Tool.icon`) e aqui esse nome
 * vira desenho.
 */

import { h } from '../render/svg.ts';
import type { ToolId } from '../geom/tools.ts';

// ---------------------------------------------------------------------------
// Primitivas de desenho
// ---------------------------------------------------------------------------

const linha = (x1: number, y1: number, x2: number, y2: number, cls?: string): SVGElement =>
  h('line', cls ? { x1, y1, x2, y2, class: cls } : { x1, y1, x2, y2 });

const caminho = (d: string, cls?: string): SVGElement => h('path', cls ? { d, class: cls } : { d });

/** Ponto que a ferramenta CRIA. */
const disco = (cx: number, cy: number, r = 2.1): SVGElement =>
  h('circle', { cx, cy, r, class: 'ic-cheio' });

/** Ponto que ja existe e serve de apoio. */
const anel = (cx: number, cy: number, r = 2.1): SVGElement => h('circle', { cx, cy, r });

const circulo = (cx: number, cy: number, r: number, cls?: string): SVGElement =>
  h('circle', cls ? { cx, cy, r, class: cls } : { cx, cy, r });

/** Ponta de seta em (x, y), apontando na direcao (dx, dy). */
function seta(x: number, y: number, dx: number, dy: number): SVGElement {
  const n = Math.hypot(dx, dy) || 1;
  const ux = dx / n;
  const uy = dy / n;
  const px = -uy;
  const py = ux;
  const L = 4.2;
  const W = 2.6;
  const a = `${x - ux * L + px * W},${y - uy * L + py * W}`;
  const b = `${x},${y}`;
  const c = `${x - ux * L - px * W},${y - uy * L - py * W}`;
  return h('polyline', { points: `${a} ${b} ${c}` });
}

// ---------------------------------------------------------------------------
// Os desenhos
// ---------------------------------------------------------------------------

const DESENHOS: Readonly<Record<string, () => SVGElement[]>> = {
  cursor: () => [caminho('M6 3.2 L6 18.4 L9.9 14.4 L12.3 20 L14.6 19 L12.2 13.6 L17.6 13.4 Z')],

  point: () => [circulo(12, 12, 6.4, 'ic-fraco'), disco(12, 12, 3.1)],

  segment: () => [linha(6, 18, 18, 6), disco(6, 18), disco(18, 6)],

  ray: () => [linha(5.5, 18.5, 17, 7), disco(5.5, 18.5), seta(19, 5, 1, -1)],

  line: () => [linha(6, 18, 18, 6), seta(20, 4, 1, -1), seta(4, 20, -1, 1), disco(12, 12, 1.8)],

  circle: () => [
    circulo(12, 13, 7.4),
    disco(12, 13, 1.9),
    linha(12, 13, 17.2, 7.8, 'ic-tracejado'),
  ],

  compass: () => [
    caminho('M12 3.4 L6.2 19.2'),
    caminho('M12 3.4 L17.8 19.2'),
    caminho('M5.4 16.4 A 8.6 8.6 0 0 0 18.6 16.4', 'ic-tracejado'),
    disco(12, 3.4, 1.7),
  ],

  intersect: () => [linha(4, 7, 20, 14), linha(6, 19, 17, 4), disco(11.6, 9.6, 2.4)],

  midpoint: () => [linha(5, 17, 19, 7), anel(5, 17, 1.8), anel(19, 7, 1.8), disco(12, 12, 2.4)],

  perp: () => [
    linha(3.5, 17.5, 20.5, 17.5, 'ic-tracejado'),
    linha(12, 3.5, 12, 17.5),
    caminho('M12 13.6 L15.9 13.6 L15.9 17.5'),
  ],

  parallel: () => [
    linha(4, 16.5, 16, 4.5, 'ic-tracejado'),
    linha(8, 19.5, 20, 7.5),
    disco(14, 13.5, 1.9),
  ],

  perpbis: () => [
    linha(4.5, 16, 19.5, 8, 'ic-tracejado'),
    anel(4.5, 16, 1.8),
    anel(19.5, 8, 1.8),
    caminho('M8.6 18.9 L15.4 5.1'),
  ],

  bisector: () => [
    caminho('M5 19 L5.6 5.4'),
    caminho('M5 19 L19.6 15.4'),
    caminho('M5 19 L18.4 6.6', 'ic-tracejado'),
    disco(5, 19, 1.9),
  ],

  reflect: () => [
    linha(12, 3, 12, 21, 'ic-tracejado'),
    anel(6.2, 12, 2.1),
    disco(17.8, 12, 2.1),
    linha(8.8, 12, 15.2, 12, 'ic-fraco'),
  ],

  undo: () => [
    caminho('M5.2 11.4 A 7.2 7.2 0 1 1 8.4 18.6'),
    caminho('M5.2 6.2 L5.2 11.6 L10.6 11.6'),
  ],

  trash: () => [
    caminho('M5.4 7.4 L18.6 7.4'),
    caminho('M9.4 7.4 L9.4 5.2 L14.6 5.2 L14.6 7.4'),
    caminho('M7 7.4 L8 19.4 L16 19.4 L17 7.4'),
    linha(10.4, 10.4, 10.4, 16.4, 'ic-fraco'),
    linha(13.6, 10.4, 13.6, 16.4, 'ic-fraco'),
  ],

  // Navegacao do quadro (nao sao ferramentas do DAG).
  mais: () => [
    circulo(11, 11, 6.6),
    linha(11, 8, 11, 14),
    linha(8, 11, 14, 11),
    linha(15.8, 15.8, 20, 20),
  ],
  menos: () => [circulo(11, 11, 6.6), linha(8, 11, 14, 11), linha(15.8, 15.8, 20, 20)],
  enquadrar: () => [
    caminho('M4 8.6 L4 4 L8.6 4'),
    caminho('M15.4 4 L20 4 L20 8.6'),
    caminho('M20 15.4 L20 20 L15.4 20'),
    caminho('M8.6 20 L4 20 L4 15.4'),
    disco(12, 12, 1.9),
  ],
};

/** Devolve o icone como SVG. `nome` vem de `Tool.icon` ou dos glifos de navegacao. */
export function icone(nome: string): SVGElement {
  const fabrica = DESENHOS[nome] ?? DESENHOS['point'];
  const svg = h('svg', {
    viewBox: '0 0 24 24',
    class: 'pad-icone',
    'aria-hidden': 'true',
    focusable: 'false',
  });
  for (const el of (fabrica as () => SVGElement[])()) svg.appendChild(el);
  return svg;
}

// ---------------------------------------------------------------------------
// Metadados de interface das ferramentas
// ---------------------------------------------------------------------------

/**
 * Tres grupos, na ordem em que a mao os procura: o que TRACA (regua e compasso), o
 * que DERIVA (constroi um objeto a partir de outros) e o que CONTROLA (selecionar,
 * apagar, desfazer). O cinto de cada item e um subconjunto disto, e a barra so mostra
 * os grupos que aquele item realmente tem.
 */
export type GrupoFerramenta = 'tracar' | 'derivar' | 'controle';

export interface MetaFerramenta {
  readonly grupo: GrupoFerramenta;
  /** Tecla, em minuscula. */
  readonly atalho: string;
  readonly atalhoRotulo: string;
  /** Como o gesto de arrastar resolve esta ferramenta, em uma frase. */
  readonly gesto: string;
}

export const META: Readonly<Record<ToolId, MetaFerramenta>> = Object.freeze({
  select: {
    grupo: 'controle',
    atalho: 'v',
    atalhoRotulo: 'V',
    gesto: 'arraste para mover o quadro',
  },
  point: {
    grupo: 'tracar',
    atalho: 'p',
    atalhoRotulo: 'P',
    gesto: 'toque, ou arraste para mirar antes de soltar',
  },
  segment: {
    grupo: 'tracar',
    atalho: 's',
    atalhoRotulo: 'S',
    gesto: 'arraste de uma ponta a outra',
  },
  ray: {
    grupo: 'tracar',
    atalho: 'e',
    atalhoRotulo: 'E',
    gesto: 'arraste da origem para onde ela aponta',
  },
  line: { grupo: 'tracar', atalho: 'r', atalhoRotulo: 'R', gesto: 'arraste de um ponto ao outro' },
  'circle-center-through': {
    grupo: 'tracar',
    atalho: 'c',
    atalhoRotulo: 'C',
    gesto: 'arraste do centro para abrir o raio',
  },
  compass: {
    grupo: 'tracar',
    atalho: 'o',
    atalhoRotulo: 'O',
    gesto: 'toque no centro, depois arraste a abertura de um ponto ao outro',
  },
  intersect: {
    grupo: 'derivar',
    atalho: 'i',
    atalhoRotulo: 'I',
    gesto: 'arraste de um objeto ao outro',
  },
  midpoint: {
    grupo: 'derivar',
    atalho: 'm',
    atalhoRotulo: 'M',
    gesto: 'arraste de um ponto ao outro',
  },
  perpendicular: {
    grupo: 'derivar',
    atalho: 'x',
    atalhoRotulo: 'X',
    gesto: 'arraste do ponto até a reta',
  },
  parallel: {
    grupo: 'derivar',
    atalho: 'l',
    atalhoRotulo: 'L',
    gesto: 'arraste do ponto até a reta',
  },
  'perp-bisector': {
    grupo: 'derivar',
    atalho: 't',
    atalhoRotulo: 'T',
    gesto: 'arraste de um ponto ao outro',
  },
  'angle-bisector': {
    grupo: 'derivar',
    atalho: 'b',
    atalhoRotulo: 'B',
    gesto: 'toque num lado, depois arraste do vértice até o outro lado',
  },
  reflect: {
    grupo: 'derivar',
    atalho: 'f',
    atalhoRotulo: 'F',
    gesto: 'arraste do ponto até o eixo',
  },
  undo: { grupo: 'controle', atalho: 'u', atalhoRotulo: 'Ctrl+Z', gesto: 'volta um passo' },
  delete: { grupo: 'controle', atalho: 'd', atalhoRotulo: 'Del', gesto: 'toque no que deve sair' },
});
